import { Node, NodePositionChange } from "reactflow"
import { GraphNodeData, GroupNodeData } from "@store/services/converter/types"
import { NodeBase, getOverlappingArea, nodeToRect } from "@xyflow/system"
import { groupNodeDragChanges } from "@features/graph/changes/groupNodeDragChanges"
import { GraphNodeChange } from "@features/graph/changes/graphApplyChanges"
import { parentNodeDragChanges } from "@features/graph/changes/parentNodeDragChanges"

export const graphPositionChangeHandler = (
  change: NodePositionChange,
  nodeInternals: Map<string, Node>,
): GraphNodeChange[] => {
  // group nodes starts with g<number>
  if (change.id.startsWith("g")) {
    return changeGroupPosition(change, nodeInternals)
  }
  return changeNodePosition(change, nodeInternals)
}

const changeNodePosition = (
  change: NodePositionChange,
  nodeInternals: Map<string, Node>,
): GraphNodeChange[] => {
  const node: Node<GraphNodeData> | undefined = nodeInternals.get(change.id)!
  const nodeChange = { id: node.id, position: change.position }
  // this is node without groups - we want to just return this as a change
  if (!node.data.display.groups || node.data.display.groups.length === 0) {
    return [nodeChange]
  }
  const lastGroup = node.data.display.groups[node.data.display.groups.length - 1]
  // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
  const parent = nodeInternals.get(lastGroup)!
  const parentChange = changeParentNodePosition(parent, node)
  return parentChange ? [parentChange, nodeChange] : [nodeChange]
}

const changeGroupPosition = (
  change: NodePositionChange,
  nodeInternals: Map<string, Node>,
): GraphNodeChange[] => {
  if (!change.position) return []
  const changes = groupNodeDragChanges(change, nodeInternals)
  const node: Node<GroupNodeData> | undefined = nodeInternals.get(change.id)
  // node have no parent so we don't care about resizing parent
  if (!node || !node.data.group.parent) {
    return changes
  }
  const parent = nodeInternals.get(node.data.group.parent)!
  const parentChange = changeParentNodePosition(parent, node)
  if (parentChange) changes.push(parentChange)

  return changes
}

const changeParentNodePosition = (parent: Node, node: Node): GraphNodeChange | undefined => {
  // calculate expanding parent group when we drag node
  const parentRect = nodeToRect(parent as NodeBase)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const nodeRect = nodeToRect(node as any as NodeBase)
  const area = getOverlappingArea(nodeRect, parentRect)
  if (area < nodeRect.width * nodeRect.height) {
    return parentNodeDragChanges(parent, parentRect, node, nodeRect)
  }
  return undefined
}
